
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { Check } from "lucide-react"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 font-sans",
  {
    variants: {
      variant: {
        choice: "choice-button",
        default: "bg-gradient-to-r from-brand-blue to-brand-magenta text-white shadow-lg shadow-brand-blue/25 hover:shadow-xl hover:shadow-brand-blue/30 hover:scale-[1.02] border border-brand-blue/20",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-lg hover:shadow-xl",
        outline:
          "border border-white/30 bg-white/10 backdrop-blur-sm text-white shadow-lg hover:bg-white/20 hover:border-white/40 transition-all duration-300 hover:scale-[1.02]",
        secondary:
          "bg-white/10 backdrop-blur-sm text-white shadow-lg border border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-[1.02]",
        ghost: "hover:bg-white/10 text-white/90 hover:text-white transition-all duration-300",
        link: "text-brand-blue underline-offset-4 hover:underline hover:text-brand-blue-dark transition-all duration-200",
        "panel-select": "bg-white/10 backdrop-blur-sm text-white shadow-md hover:bg-gradient-to-r hover:from-brand-blue hover:to-brand-magenta hover:text-white border border-white/20 hover:shadow-lg hover:shadow-brand-blue/20 transition-all duration-300 hover:scale-[1.02]",
        "search-button": "bg-gradient-to-r from-brand-blue to-brand-magenta text-white shadow-lg shadow-brand-blue/25 hover:shadow-xl hover:shadow-brand-blue/30 hover:scale-[1.02] transition-all duration-300",
        elegant: "bg-white/10 backdrop-blur-sm border border-white/20 shadow-lg text-white hover:bg-white/20 hover:border-white/30 hover:shadow-xl transition-all duration-300 hover:scale-[1.01]",
        "elegant-primary": "bg-gradient-to-r from-brand-blue to-brand-magenta text-white shadow-lg shadow-brand-blue/30 hover:shadow-xl hover:shadow-brand-blue/40 hover:scale-[1.02] border border-white/20 backdrop-blur-sm transition-all duration-300"
      },
      size: {
        default: "h-10 px-4 py-2 [@media(pointer:coarse)]:min-h-[44px]",
        sm: "h-9 rounded-md px-3 [@media(pointer:coarse)]:min-h-[44px]",
        lg: "h-11 rounded-md px-8 [@media(pointer:coarse)]:min-h-[44px]",
        icon: "h-10 w-10 [@media(pointer:coarse)]:min-h-[44px] [@media(pointer:coarse)]:min-w-[44px]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const isChoice = variant === "choice" || typeof props["aria-pressed"] === "boolean" || typeof props["aria-selected"] === "boolean"
    const selected = props["aria-pressed"] === true || props["aria-selected"] === true
    return (
      <Comp
        className={cn(buttonVariants({ variant: isChoice ? "choice" : variant, size, className }), isChoice && "choice-button")}
        ref={ref}
        {...props}
      >
        {isChoice && !asChild && <Check aria-hidden="true" className={selected ? "opacity-100" : "opacity-0"} />}
        {children}
      </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
